import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, Empty } from "@/components/common";
import { StatusBadge } from "@/lib/status";
import { Download, Loader2 } from "lucide-react";

export default function MySubmissionsPage() {
  const { user } = useAuth();
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [positionsMap, setPositionsMap] = useState({});
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [c, p] = await Promise.all([
          api.get("/candidates", { params: { recruiter_email: user.email } }),
          api.get("/positions"),
        ]);
        setCandidates(c.data);
        const map = {};
        p.data.forEach((pos) => { map[pos.id] = pos; });
        setPositionsMap(map);
      } catch (e) { setError(formatErr(e)); }
      finally { setLoading(false); }
    })();
  }, [user.email]);

  const downloadResume = async (cand) => {
    try {
      const res = await api.get(`/candidates/${cand.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = cand.resume_file_name || `${cand.name}.pdf`;
      a.click(); window.URL.revokeObjectURL(url);
    } catch (e) { alert(formatErr(e)); }
  };

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <PageHeader testId="submissions-header" title="My Submissions" subtitle="Every candidate you've submitted." />
      {error && <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</div>}

      <div className="card-base">
        {loading ? <div className="p-8 text-gray-500 text-sm flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Loading…</div>
          : candidates.length === 0 ? <div className="p-6"><Empty title="No submissions yet" subtitle="Submit your first candidate from the Open Positions page." /></div>
          : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="submissions-table">
                <thead className="bg-gray-50">
                  <tr>
                    <Th>Candidate</Th><Th>Position</Th><Th>Contact</Th><Th>Rate</Th><Th>Submitted</Th><Th>Status</Th><Th>Resume</Th>
                  </tr>
                </thead>
                <tbody>
                  {candidates.map((c) => {
                    const pos = positionsMap[c.position_id];
                    return (
                      <tr key={c.id} className="border-t border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4 font-medium">{c.name}</td>
                        <td className="py-3 px-4">
                          <div className="text-gray-900">{pos?.position_name || "—"}</div>
                          <div className="text-xs text-gray-500">{pos?.client_company}</div>
                        </td>
                        <td className="py-3 px-4">
                          <div>{c.email}</div>
                          <div className="text-xs text-gray-500">{c.phone}</div>
                        </td>
                        <td className="py-3 px-4 font-mono text-xs">{c.rate || "—"}</td>
                        <td className="py-3 px-4 text-xs text-gray-500">{new Date(c.submitted_at).toLocaleDateString()}</td>
                        <td className="py-3 px-4"><StatusBadge status={c.status || "submitted"} /></td>
                        <td className="py-3 px-4">
                          {c.resume_file_name ? (
                            <button onClick={() => downloadResume(c)} className="text-brand hover:underline text-xs inline-flex items-center gap-1" data-testid={`download-resume-${c.id}`}>
                              <Download size={12} /> {c.resume_file_name}
                            </button>
                          ) : <span className="text-xs text-gray-400">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
      </div>
    </div>
  );
}

function Th({ children }) {
  return <th className="text-left text-xs font-semibold uppercase tracking-wider text-gray-500 py-3 px-4">{children}</th>;
}
